import { OrgType } from '@/types/organizations'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<OrgType>({
  pathCategory: 'org_types',
})

export function useGetOrgType({
  id,
  ...options
}: UseGetDetailOptions<OrgType>) {
  const query = useGetDetail<OrgType>({
    id,
    queryKey: queryKeys.orgTypes.detail({ id, filters: options.filters }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    orgType: query.data,
    ...query,
  }
}

export function useCreateOrgType(
  options: UseCreateDetailOptions<OrgType> = {},
) {
  const mutation = useCreateDetail<OrgType>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createOrgType: mutation.mutate,
    createOrgTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateOrgType(
  options: UseUpdateDetailOptions<OrgType> = {},
) {
  const mutation = useUpdateDetail<OrgType>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateOrgType: mutation.mutate,
    updateOrgTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteOrgType(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<OrgType>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteOrgType: mutation.mutate,
    deleteOrgTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetOrgTypes({
  filters,
  ...options
}: UseGetListOptions<OrgType> = {}) {
  const query = useGetList<OrgType>({
    filters,
    queryKey: queryKeys.orgTypes.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    orgTypes: query.allData,
    ...query,
  }
}

export function useUpdateOrgTypes(options: UseUpdateListOptions<OrgType> = {}) {
  const mutation = useUpdateList<OrgType>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateOrgTypes: mutation.mutate,
    updateOrgTypesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteOrgTypes({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<OrgType>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteOrgTypes: mutation.mutate,
    deleteOrgTypesAsync: mutation.mutateAsync,
    ...mutation,
  }
}
